import Vue from 'vue';
import { TRIAL_CONFIG } from '@/constants/trial';

const state = {
    loggedIn: false,
    admin: false,
    fullMember: false,
    v1Token: null,    // V1 API token
    v2Token: null,    // V2 API token
    roles: [],
    trialStatus: {
        isTrialUser: false,
        trialEndDate: null,
        isTrialExpired: false,
        trialEntriesUsed: 0,
        trialEntriesLimit: TRIAL_CONFIG.ENTRY_LIMIT,
        accountCreatedDate: null,
        deletionDate: null,
    },
    scheduledDeletionDate: null,
    totalEntries: 0
};

// getters
const getters = {
    isLoggedIn: (state) => {
        return state.loggedIn;
    },
    isAdmin: (state) => {
        return state.loggedIn && state.admin
    },
    isFullMember: (state) => {
        return state.fullMember;
    },
    isOrganisationManager: (state) => {
        if (state.roles) {
            return state.roles.indexOf('Organisation-Manager') > -1;
        }
        return false;
    },
    getV1Token: (state) => {
        return state.v1Token;
    },
    getV2Token: (state) => {
        return state.v2Token;
    },
    getRoles: (state) => {
        return state.roles;
    },
    isTrialUser: (state) => {
        return state.trialStatus.isTrialUser;
    },
    isTrialExpired: (state) => {
        return state.trialStatus.isTrialExpired;
    },
    getTrialStatus: (state) => {
        return state.trialStatus;
    },
    canAccessFeatures: (state) => {
        // Premium users always have access
        if (state.fullMember) return true;
        
        // Trial users have access during active trial period
        if (state.trialStatus.isTrialUser && !state.trialStatus.isTrialExpired) {
            return true;
        }
        
        // All other users are locked (legacy free or post-trial)
        return false;
    },
    getTrialEntriesRemaining: (state) => {
        return Math.max(0, state.trialStatus.trialEntriesLimit - state.trialStatus.trialEntriesUsed);
    },
    getScheduledDeletionDate: (state) => {
        return state.scheduledDeletionDate;
    }
};

// actions
const actions = {
    storeV1Token: ({commit}, {token}) => {
        commit('setV1Token', token);
    },
    storeV2Token: ({commit}, {token}) => {
        commit('setV2Token', token);
    },
    storeLoginDetails: ({commit, dispatch}, {token, isAdmin, roles}) => {
        commit('storeLoginDetails', {token, isAdmin, roles});
        commit('setLoggedIn', true);
        commit('configureHttpClient');

        dispatch("intercom/boot", null, { root: true });
    },
    setFullMembershipStatus: ({commit}, {fullMember}) => {
        commit('setFullMembershipStatus', {fullMember});
    },
    setTrialStatus: ({commit}, trialData) => {
        commit('setTrialStatus', trialData);
    },
    updateTrialEntryCount: ({commit}, count) => {
        commit('updateTrialEntryCount', count);
    },
    setScheduledDeletionDate: ({commit}, date) => {
        commit('setScheduledDeletionDate', date);
    },
    logout: ({commit, dispatch}) => {
        commit('clearAllTokens');
        commit('setLoggedIn', false);
        Vue.http.headers.common['Authorization'] = null;
        dispatch("intercom/reboot", null, { root: true });
        // Clear user-specific profile data to prevent data leakage between users
        dispatch("userProfile/clearProfile", null, { root: true });
    },
    configureHttpClient: ({commit}) => {
        commit('configureHttpClient');
    }
};

// mutations
const mutations = {
    setV1Token(state, token) {
        state.v1Token = token;
    },
    setV2Token(state, token) {
        state.v2Token = token;
    },
    clearAllTokens(state) {
        state.v1Token = null;
        state.v2Token = null;
    },
    storeLoginDetails(state, {token, isAdmin, roles}) {
        // Only store admin and roles info - tokens are handled separately
        state.admin = isAdmin;
        state.roles = roles;
        state.loggedIn = true;
    },
    setFullMembershipStatus(state, {fullMember}) {
        state.fullMember = fullMember;
    },
    setTrialStatus(state, trialData) {
        state.trialStatus = { ...state.trialStatus, ...trialData };
    },
    updateTrialEntryCount(state, count) {
        state.trialStatus.trialEntriesUsed = count;
    },
    setScheduledDeletionDate(state, date) {
        state.scheduledDeletionDate = date;
    },
    setTotalEntries(state, count) {
        state.totalEntries = count;
    },
    setLoggedIn(state, loggedIn) {
        state.loggedIn = loggedIn;
        if (!loggedIn) {
            state.v1Token = null;
            state.v2Token = null;
            state.admin = false;
        }
    },
    configureHttpClient: (state) => {
        // Clear any default authorization header since we handle tokens per request in the API utilities
        Vue.http.headers.common['Authorization'] = null;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}